import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.12 },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1516794267396-7ca9db9ea79f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwcmVzb3J0JTIwZ2xhbXBpbmclMjBuaWdodCUyMHN0YXJzfGVufDB8fHx8MTc4NTY4MzgxN3ww&ixlib=rb-4.1.0&q=85";

export const Hero = ({ onApply }) => {
  const lenis = useLenis();
  const scrollTo = (href) => {
    if (lenis) lenis.scrollTo(href, { offset: -80, duration: 1.2 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="top" className="relative h-[100svh] min-h-[680px] w-full overflow-hidden">
      <motion.div
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img src={HERO_IMG} alt="Luxury glamping destination at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-oed-base via-oed-base/40 to-oed-base/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-oed-base/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-oed-gold mb-6"
        >
          Own Your Territory — Destination Dealership
        </motion.p>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          {["Own Your", "Destination"].map((t, i) => (
            <span key={i} className="mask-line">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {t}
              </motion.span>
            </span>
          ))}
          <span className="mask-line">
            <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-oed-gold">
              Dealership
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          Lead the future of off-grid living, tiny-home communities and luxury glamping resorts.
          Build a business that creates wealth, freedom & legacy.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-apply-button"
            onClick={onApply}
            className="bg-white text-black px-8 py-4 text-sm font-medium tracking-wide hover:bg-oed-gold transition-colors"
          >
            Claim Your Territory
          </button>
          <a
            href="#opportunity"
            data-testid="hero-explore-button"
            onClick={(e) => { e.preventDefault(); scrollTo("#opportunity"); }}
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-sm text-white/80 hover:bg-white/10 transition-colors"
          >
            Explore the Opportunity
            <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 border border-white/15 bg-black/30 backdrop-blur-xl px-6 py-5"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Investment from</p>
        <p className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-1">$150,000</p>
      </motion.div>
    </section>
  );
};
